import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Table, Modal, Button } from "react-bootstrap";
import { FaChartLine, FaRegCalendarAlt, FaMugHot, FaTags, FaDownload, FaInfoCircle } from "react-icons/fa";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import * as XLSX from "xlsx";
import { api } from "../../config/api";
import { format, subDays } from "date-fns";
import DateRangeSelector from "./DateRangeSelector";
import SharedOrderDetailModal from "../orders/SharedOrderDetailModal";

export default function SalesReport() {
  const [salesData, setSalesData] = useState([]);
  const [menuData, setMenuData] = useState([]);
  const [discountData, setDiscountData] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  const [loading, setLoading] = useState(false);

  // Discount breakdown state
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [selectedDiscountName, setSelectedDiscountName] = useState("");
  const [discountBreakdownData, setDiscountBreakdownData] = useState([]);
  const [discountLoading, setDiscountLoading] = useState(false);

  // Order Detail modal state
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);

  const openDiscountBreakdown = async (discountName) => {
    setSelectedDiscountName(discountName);
    setShowDiscountModal(true);
    setDiscountLoading(true);
    try {
      const token = localStorage.getItem("token");
      const qs = `?name=${encodeURIComponent(discountName)}&startDate=${dateRange.startDate} 00:00:00&endDate=${dateRange.endDate} 23:59:59`;
      const res = await api.get(`/api/reports/discounts/breakdown${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiscountBreakdownData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setDiscountLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!discountBreakdownData || discountBreakdownData.length === 0) return;
    
    const dataForExport = discountBreakdownData.map(item => ({
      'Date': format(new Date(item.date), 'dd MMM yyyy, HH:mm'),
      'Order ID': item.order_id,
      'Discount Amount (฿)': Number(item.amount)
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataForExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Breakdown");
    XLSX.writeFile(workbook, `discount_breakdown_${format(new Date(), 'yyyyMMdd')}.xlsx`);
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const qs = `?startDate=${dateRange.startDate} 00:00:00&endDate=${dateRange.endDate} 23:59:59`;
      const [salesRes, menuRes, discountRes] = await Promise.all([
        api.get(`/api/reports/sales${qs}`, { headers }),
        api.get(`/api/reports/menu-frequency${qs}`, { headers }),
        api.get(`/api/reports/discounts${qs}`, { headers })
      ]);
      
      // format sales date for chart
      const formattedSales = salesRes.data.map(item => ({
        ...item,
        dateFormatted: format(new Date(item.date), 'dd MMM'),
        total_revenue: Number(item.total_revenue)
      }));

      setSalesData(formattedSales);
      setMenuData(menuRes.data);
      setDiscountData(discountRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [dateRange]);

  const totalRevenue = salesData.reduce((acc, curr) => acc + curr.total_revenue, 0);
  const totalOrders = salesData.reduce((acc, curr) => acc + curr.total_orders, 0);
  const avgPerDay = salesData.length > 0 ? (totalRevenue / salesData.length).toFixed(2) : 0;

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold mb-0" style={{ color: "#1e3c72" }}>
          <FaChartLine className="me-2" /> Sales & Revenue Report
        </h4>
        <div className="d-flex gap-2">
          <DateRangeSelector dateRange={dateRange} onChange={setDateRange} />
        </div>
      </div>

      {/* Summary Cards */}
      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="border-0 shadow-sm text-center" style={{ borderRadius: '12px', background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white' }}>
            <Card.Body>
              <h6 className="text-uppercase mb-2 opacity-75">Total Revenue</h6>
              <h3 className="fw-bold mb-0">฿{totalRevenue.toLocaleString(undefined, {minimumFractionDigits: 2})}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm text-center" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <h6 className="text-uppercase mb-2 text-muted">Total Orders</h6>
              <h3 className="fw-bold mb-0 text-primary">{totalOrders}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm text-center" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <h6 className="text-uppercase mb-2 text-muted">Avg Revenue / Day</h6>
              <h3 className="fw-bold mb-0 text-success">฿{Number(avgPerDay).toLocaleString()}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row className="g-4 mb-4">
        <Col lg={12}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <h6 className="fw-bold text-muted mb-4"><FaRegCalendarAlt className="me-2"/>Daily Revenue Trend</h6>
              <div style={{ height: '300px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={salesData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                    <XAxis dataKey="dateFormatted" axisLine={false} tickLine={false} tick={{fill: '#888', fontSize: 12}} />
                    <YAxis axisLine={false} tickLine={false} tick={{fill: '#888', fontSize: 12}} tickFormatter={(value) => `฿${value}`} />
                    <RechartsTooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
                    <Line type="monotone" dataKey="total_revenue" name="Revenue (฿)" stroke="#1e3c72" strokeWidth={3} dot={{r: 4, fill: '#1e3c72', strokeWidth: 2, stroke: '#fff'}} activeDot={{r: 6}} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4">
        {/* Menu Frequency */}
        <Col lg={7}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <h6 className="fw-bold text-muted mb-3"><FaMugHot className="me-2"/>Top Selling Menu Items</h6>
              <Table responsive hover className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="border-0 rounded-start">Item Name</th>
                    <th className="border-0 text-center">Qty Sold</th>
                    <th className="border-0 text-end rounded-end">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {menuData.slice(0, 10).map((item, idx) => (
                    <tr key={idx}>
                      <td className="fw-medium text-dark">{item.name}</td>
                      <td className="text-center">
                        <span className="badge bg-primary rounded-pill px-3">{item.total_quantity_sold}</span>
                      </td>
                      <td className="text-end text-success fw-bold">฿{Number(item.total_revenue).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                    </tr>
                  ))}
                  {menuData.length === 0 && <tr><td colSpan="3" className="text-center text-muted py-3">No data available</td></tr>}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        {/* Discounts */}
        <Col lg={5}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <h6 className="fw-bold text-muted mb-3">
                <FaTags className="me-2"/>Discount Usage <FaInfoCircle className="ms-1" size={12}/>
              </h6>
              <Table responsive hover className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="border-0 rounded-start">Discount</th>
                    <th className="border-0 text-center">Used</th>
                    <th className="border-0 text-end rounded-end">Total Given</th>
                  </tr>
                </thead>
                <tbody>
                  {discountData.map((item, idx) => (
                    <tr 
                      key={idx} 
                      style={{ cursor: 'pointer', transition: 'background-color 0.2s' }} 
                      onClick={() => openDiscountBreakdown(item.discount_name)}
                      className="hover-bg-light"
                    >
                      <td className="fw-medium text-primary text-decoration-underline">{item.discount_name}</td>
                      <td className="text-center">{item.usage_count} times</td>
                      <td className="text-end text-danger fw-bold">-฿{Number(item.total_discount_value).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                    </tr>
                  ))}
                  {discountData.length === 0 && <tr><td colSpan="3" className="text-center text-muted py-3">No data available</td></tr>}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Modal show={showDiscountModal} onHide={() => setShowDiscountModal(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-light align-items-center">
          <div className="d-flex align-items-center gap-3">
            <Modal.Title className="h5 mb-0 fw-bold text-primary">
              Discount Usage: {selectedDiscountName}
            </Modal.Title>
            <Button 
              variant="outline-success" 
              size="sm" 
              className="d-flex align-items-center px-3 fw-medium" 
              onClick={exportToExcel} 
              disabled={discountLoading || discountBreakdownData.length === 0}
            >
              <FaDownload className="me-2" /> Export Excel
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div style={{ maxHeight: '400px', overflowY: 'auto', borderBottomLeftRadius: 'calc(var(--bs-modal-border-radius, 0.5rem) - 1px)', borderBottomRightRadius: 'calc(var(--bs-modal-border-radius, 0.5rem) - 1px)' }}>
            <Table responsive hover className="align-middle mb-0">
              <thead className="table-light sticky-top">
                <tr>
                  <th className="border-0 rounded-start px-4">Date</th>
                  <th className="border-0">Order ID</th>
                  <th className="border-0 text-end rounded-end px-4">Discount Given</th>
                </tr>
              </thead>
              <tbody>
                {discountLoading ? (
                  <tr><td colSpan={3} className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></td></tr>
                ) : (
                  <>
                    {discountBreakdownData.map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-muted px-4">{format(new Date(item.date), 'dd MMM yyyy, HH:mm')}</td>
                        <td 
                          className="fw-medium" 
                          style={{ cursor: 'pointer', color: '#0d6efd', textDecoration: 'underline' }} 
                          onClick={() => { setSelectedOrderId(item.order_id); setShowOrderModal(true); }}
                        >
                          #{item.order_id}
                        </td>
                        <td className="text-end fw-bold text-danger px-4">
                          -฿{Number(item.amount).toLocaleString(undefined, {minimumFractionDigits: 2})}
                        </td>
                      </tr>
                    ))}
                    {discountBreakdownData.length === 0 && <tr><td colSpan={3} className="text-center text-muted py-4">No records found.</td></tr>}
                  </>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>

      <SharedOrderDetailModal 
        show={showOrderModal} 
        onHide={() => setShowOrderModal(false)} 
        orderId={selectedOrderId} 
        readOnly={true}
      />
    </Container>
  );
}
